import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import {
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_PAUSED,
  RECORDING_STATE_SAVING,
  RECORDING_STATE_STOPPED
} from "./medical-scribe-constants";
import {
  RECORDING_GAP_NOTICE_THRESHOLD_MS,
  type RecordingInterruption,
  mergeInterruptions
} from "./streaming/recording-interruption.util";

export interface TranscriptEntry {
  speaker: number;
  text: string;
  isFinal: boolean;
  timestamp?: number;
}

// Live coaching activation lifecycle (ENG-1705). "off" is a provider choice and
// sticks for the rest of the recording unless undone; every failure path lands
// back on "idle" so the UI reads as if coaching was never attempted.
export type CoachSessionStatus = "idle" | "activating" | "active" | "off";

export interface CoachCue {
  id: string;
  /** Backend CoachEvent id from the WS frame — absent when the backend's
   * best-effort event write failed, in which case feedback can't be recorded. */
  cueId?: number;
  category: string;
  priority: string;
  headline: string;
  advice: string;
  suggestedActions: string[];
  decaySeconds: number;
  receivedAt: number;
  dismissed?: boolean;
  feedback?: "up" | "down";
}

interface MedicalScribeState {
  recordingState: number;
  coachCues: CoachCue[];
  /** SN-19902: gaps in this tab's recording that never reached the transcript. */
  recordingInterruptions: RecordingInterruption[];
  coachSessionStatus: CoachSessionStatus;
  coachSessionId: string | undefined;
  chartNoteId: number | undefined;
  patientId: number | undefined;
  showSilenceWarningDialog?: boolean;
  //
  isConnecting?: boolean;
  isAuthenticated?: boolean;
  isReconnecting?: boolean;
  isPaused?: boolean;
  error?: string;
  wasResumed?: boolean;
  scribeId: number | undefined;
  transcript?: TranscriptEntry[];
  currentSessionNumber?: number;
  //
  audioUrl: string | undefined;
  showDeviceSettingsDialog: boolean;
  volume: number;
  isMicActive: boolean;
  deviceSettings: {
    microphone: string;
    speaker: string;
    showTranscriptionPreview?: boolean;
    autoGenerateSOAPNotes?: boolean;
    autoFindRelatedChartNote?: boolean;
  };
  isConfirmationDialogOpen: boolean;
  scribeFeedbackDialog?: {
    feedbackType?: number;
    scribeId?: number;
    rating?: number;
    open?: boolean;
  };
}

const medicalScribeMicrophoneStorage = setupLocalStorageAPI(
  "medicalScribeMicrophone"
);

const medicalScribeSpeakerStorage = setupLocalStorageAPI(
  "medicalScribeSpeaker"
);

const medicalScribeShowTranscriptionPreviewStorage = setupLocalStorageAPI(
  "medicalScribeShowTranscriptionPreview"
);

const medicalScribeAutoGenerateSOAPNotesStorage = setupLocalStorageAPI(
  "medicalScribeAutoGenerateSOAPNotes"
);

const medicalScribeAutoFindRelatedChartNoteStorage = setupLocalStorageAPI(
  "medicalScribeAutoFindRelatedChartNote"
);

const initialState: MedicalScribeState = {
  recordingState: RECORDING_STATE_STOPPED,
  coachCues: [],
  recordingInterruptions: [],
  coachSessionStatus: "idle",
  coachSessionId: undefined,
  chartNoteId: undefined,
  patientId: undefined,
  showSilenceWarningDialog: false,
  isConnecting: false,
  isAuthenticated: false,
  isReconnecting: false,
  isPaused: false,
  error: undefined,
  wasResumed: false,
  scribeId: undefined,
  transcript: [],
  currentSessionNumber: undefined,
  audioUrl: undefined,
  showDeviceSettingsDialog: false,
  volume: 0,
  isMicActive: false,
  deviceSettings: {
    microphone: medicalScribeMicrophoneStorage.getData("default"),
    speaker: medicalScribeSpeakerStorage.getData("default"),
    showTranscriptionPreview:
      medicalScribeShowTranscriptionPreviewStorage.getData(true),
    autoGenerateSOAPNotes:
      medicalScribeAutoGenerateSOAPNotesStorage.getData(true),
    autoFindRelatedChartNote:
      medicalScribeAutoFindRelatedChartNoteStorage.getData(true)
  },
  isConfirmationDialogOpen: false,
  scribeFeedbackDialog: {
    open: false
  }
};

export const medicalScribeSlice = createSlice({
  name: "medicalScribe",
  initialState,
  reducers: {
    changeScribeDeviceSettings: (
      state,
      action: PayloadAction<{
        microphone: string;
        speaker: string;
        showTranscriptionPreview?: boolean;
        autoGenerateSOAPNotes?: boolean;
        autoFindRelatedChartNote?: boolean;
      }>
    ) => {
      const {
        microphone,
        speaker,
        showTranscriptionPreview,
        autoGenerateSOAPNotes,
        autoFindRelatedChartNote
      } = action.payload;

      state.deviceSettings = {
        microphone,
        speaker,
        showTranscriptionPreview: showTranscriptionPreview ?? true,
        autoGenerateSOAPNotes: autoGenerateSOAPNotes ?? true,
        autoFindRelatedChartNote: autoFindRelatedChartNote ?? true
      };
      medicalScribeMicrophoneStorage.updateData(microphone);
      medicalScribeSpeakerStorage.updateData(speaker);
      medicalScribeShowTranscriptionPreviewStorage.updateData(
        showTranscriptionPreview ?? true
      );
      medicalScribeAutoGenerateSOAPNotesStorage.updateData(
        autoGenerateSOAPNotes ?? true
      );
      medicalScribeAutoFindRelatedChartNoteStorage.updateData(
        autoFindRelatedChartNote ?? true
      );
    },
    startScribeRecording: (
      state,
      action: PayloadAction<{ patientId?: number; chartNoteId?: number }>
    ) => {
      const { patientId, chartNoteId } = action.payload;
      state.recordingState = RECORDING_STATE_IN_PROGRESS;
      state.chartNoteId = chartNoteId;
      state.patientId = patientId;

      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }
    },
    setShowSilenceWarningDialog: (state, action: PayloadAction<boolean>) => {
      state.showSilenceWarningDialog = action.payload;
    },

    pauseScribeRecording: (state) => {
      state.recordingState = RECORDING_STATE_PAUSED;
    },
    savingInProgressScribeRecording: (state) => {
      state.recordingState = RECORDING_STATE_SAVING;
    },
    resumeScribeRecording: (state) => {
      state.recordingState = RECORDING_STATE_IN_PROGRESS;
    },
    stopScribeRecording: (state, action: PayloadAction<string>) => {
      // deviceSettings is a persisted user preference, not per-recording state. initialState
      // was captured at module load, so spreading it here reverted a mid-session mic change
      // back to whatever localStorage held at page load — the next recording then silently
      // used the old device. localStorage already holds the current value either way.
      return {
        ...initialState,
        deviceSettings: state.deviceSettings,
        audioUrl: action.payload
      };
    },
    updateScribeVolume: (
      state,
      action: PayloadAction<{ volume: number; isMicActive: boolean }>
    ) => {
      const { volume, isMicActive } = action.payload;
      state.volume = volume;
      state.isMicActive = isMicActive;
    },
    toggleShowDeviceSettingsDialog: (state) => {
      state.showDeviceSettingsDialog = !state.showDeviceSettingsDialog;
    },
    setOpenConfirmationDialog: (
      state,
      action: PayloadAction<boolean | undefined>
    ) => {
      const value = action.payload;

      if (value === undefined) {
        state.isConfirmationDialogOpen = !state.isConfirmationDialogOpen;
      } else {
        state.isConfirmationDialogOpen = Boolean(value);
      }
    },
    // Streaming-specific actions
    setStreamingConnecting: (state, action: PayloadAction<boolean>) => {
      state.isConnecting = action.payload;
    },
    setStreamingAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setStreamingReconnecting: (state, action: PayloadAction<boolean>) => {
      state.isReconnecting = action.payload;
    },
    setStreamingPaused: (state, action: PayloadAction<boolean>) => {
      state.isPaused = action.payload;
    },
    setStreamingError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    setStreamingScribeId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.scribeId = action.payload;
    },
    setStreamingWasResumed: (state, action: PayloadAction<boolean>) => {
      state.wasResumed = action.payload;
    },
    setStreamingSessionNumber: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.currentSessionNumber = action.payload;
    },
    setStreamingTranscript: (
      state,
      action: PayloadAction<TranscriptEntry[]>
    ) => {
      state.transcript = action.payload;
    },
    appendStreamingTranscript: (
      state,
      action: PayloadAction<TranscriptEntry>
    ) => {
      if (!state.transcript) {
        state.transcript = [];
      }
      state.transcript.push(action.payload);
    },
    clearStreamingState: (state) => {
      state.isConnecting = false;
      state.isAuthenticated = false;
      state.isReconnecting = false;
      state.isPaused = false;
      state.error = undefined;
      state.wasResumed = false;
      state.scribeId = undefined;
      state.transcript = [];
      state.currentSessionNumber = undefined;
      state.coachCues = [];
      state.coachSessionStatus = "idle";
      state.coachSessionId = undefined;
    },
    // Live coaching (CC-8): a coach_cue WS message becomes one entry, newest first.
    addCoachCue: (
      state,
      action: PayloadAction<
        Omit<CoachCue, "id" | "receivedAt" | "dismissed" | "feedback">
      >
    ) => {
      const receivedAt = Date.now();
      state.coachCues.unshift({
        ...action.payload,
        id: `${receivedAt}-${state.coachCues.length}`,
        receivedAt
      });
      // Bound the in-memory session log.
      if (state.coachCues.length > 50) {
        state.coachCues = state.coachCues.slice(0, 50);
      }
    },
    // SN-19902. Not cleared by clearStreamingState: a gap outlives the reconnect that ends it.
    // stopScribeRecording clears it with the rest of initialState.
    addRecordingInterruption: (
      state,
      action: PayloadAction<RecordingInterruption>
    ) => {
      state.recordingInterruptions = mergeInterruptions(
        state.recordingInterruptions,
        action.payload
      );
    },
    // An open gap's length is unknown until the resume that ends it. One that turns out shorter
    // than the notice threshold is dropped, like any other short gap.
    closeOpenRecordingInterruption: (state, action: PayloadAction<number>) => {
      if (!state.recordingInterruptions.some(({ toMs }) => toMs === null)) {
        return;
      }
      state.recordingInterruptions = state.recordingInterruptions
        .map((interruption) =>
          interruption.toMs === null
            ? { ...interruption, toMs: action.payload }
            : interruption
        )
        .filter(
          ({ fromMs, toMs }) =>
            (toMs ?? fromMs) - fromMs >= RECORDING_GAP_NOTICE_THRESHOLD_MS
        );
    },
    dismissRecordingInterruptions: (state) => {
      if (state.recordingInterruptions.length > 0) {
        state.recordingInterruptions = [];
      }
    },
    dismissCoachCue: (state, action: PayloadAction<string>) => {
      const cue = state.coachCues.find((c) => c.id === action.payload);
      if (cue) {
        cue.dismissed = true;
      }
    },
    setCoachCueFeedback: (
      state,
      action: PayloadAction<{ id: string; vote: "up" | "down" }>
    ) => {
      const cue = state.coachCues.find((c) => c.id === action.payload.id);
      if (cue) {
        cue.feedback = action.payload.vote;
      }
    },
    setCoachSessionActivating: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      state.coachSessionStatus = "activating";
      state.coachSessionId = action.payload.sessionId;
    },
    setCoachSessionActive: (state) => {
      // Only a pending activation may go active: a coaching_enabled ack that
      // arrives after the timeout gave up (or after the provider switched off)
      // must not resurrect a badge whose session id is already gone.
      if (state.coachSessionStatus === "activating") {
        state.coachSessionStatus = "active";
      }
    },
    setCoachSessionUnavailable: (state) => {
      state.coachSessionStatus = "idle";
      state.coachSessionId = undefined;
    },
    setCoachSessionOff: (state) => {
      state.coachSessionStatus = "off";
      state.coachSessionId = undefined;
    },
    openScribeFeedbackDialog: (
      state,
      action: PayloadAction<{
        scribeId: number;
        feedbackType: number;
        rating?: number;
      }>
    ) => {
      const { scribeId, feedbackType, rating } = action.payload;
      state.scribeFeedbackDialog = {
        scribeId,
        feedbackType,
        rating,
        open: true
      };
    },
    closeScribeFeedbackDialog: (state) => {
      state.scribeFeedbackDialog = {
        open: false
      };
    }
  }
});

export const {
  startScribeRecording,
  pauseScribeRecording,
  resumeScribeRecording,
  savingInProgressScribeRecording,
  stopScribeRecording,
  changeScribeDeviceSettings,
  toggleShowDeviceSettingsDialog,
  updateScribeVolume,
  setOpenConfirmationDialog,
  setStreamingConnecting,
  setStreamingAuthenticated,
  setStreamingReconnecting,
  setStreamingPaused,
  setStreamingError,
  setStreamingScribeId,
  setStreamingWasResumed,
  setStreamingSessionNumber,
  setStreamingTranscript,
  appendStreamingTranscript,
  clearStreamingState,
  addCoachCue,
  dismissCoachCue,
  setCoachCueFeedback,
  addRecordingInterruption,
  closeOpenRecordingInterruption,
  dismissRecordingInterruptions,
  setCoachSessionActivating,
  setCoachSessionActive,
  setCoachSessionUnavailable,
  setCoachSessionOff,
  setShowSilenceWarningDialog,
  openScribeFeedbackDialog,
  closeScribeFeedbackDialog
} = medicalScribeSlice.actions;

export default medicalScribeSlice.reducer;

const medicalScribeSelector = (state: RootState) => state.medicalScribe;

// Use medicalScribeSelector directly or specific selectors below
export const selectMedicalScribe = medicalScribeSelector;

export const selectShowMedicalScribeSilenceWarningDialog = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.showSilenceWarningDialog
);

export const selectShowMedicalScribeDeviceDialog = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.showDeviceSettingsDialog
);

export const selectIsConfirmationDialogOpen = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isConfirmationDialogOpen
);

export const selectMedicalScribeMicVolume = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.volume
);

export const selectMedicalScribeIsMicActive = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isMicActive
);

export const selectMedicalScribeDeviceSettings = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.deviceSettings
);

export const selectMedicalScribeRecordingState = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.recordingState
);

// Select patientId on its own rather than destructuring it off selectMedicalScribe:
// updateScribeVolume replaces the slice object continuously while recording, so a
// whole-slice read re-renders its consumer several times a second.
export const selectMedicalScribePatientId = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.patientId
);

// Streaming-specific selectors
export const selectStreamingIsConnecting = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isConnecting
);

export const selectStreamingIsAuthenticated = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isAuthenticated
);

export const selectStreamingIsReconnecting = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isReconnecting
);

export const selectStreamingIsPaused = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isPaused
);

export const selectStreamingError = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.error
);

export const selectStreamingScribeId = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.scribeId
);

export const selectStreamingWasResumed = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.wasResumed
);

export const selectStreamingSessionNumber = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.currentSessionNumber
);

export const selectStreamingTranscript = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.transcript
);

export const selectScribeFeedbackDialog = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.scribeFeedbackDialog
);

export const selectCoachCues = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.coachCues
);

export const selectRecordingInterruptions = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.recordingInterruptions
);

export const selectCoachSessionStatus = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.coachSessionStatus
);

export const selectCoachSessionId = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.coachSessionId
);
